import { useAuthHeader, useAuthUser } from "react-auth-kit";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosInstance from "../../utils/AxiosInstance";
import { toast } from "react-toastify";
import {
  sanitizeUserUpdatePayload,
  toastError,
  toastSuccess,
} from "../../utils/utils";

export const useCreateUser = (closeModal) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newUser) => {
      newUser.user_type = 1; // backend user, 2 sarebbe il medico

      // create user
      const resUser = await axiosInstance.post(`/users`, newUser, {
        headers: {
          Authorization: authHeader(),
        },
      });
      return resUser;
    },
    onSuccess: () => {
      toast.success("Utente aggiunto con successo!", toastSuccess);
      queryClient.invalidateQueries(["doctors"]);
      queryClient.invalidateQueries(["users"]);
      closeModal();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error || "Errore nell'aggiunta dell'utente";
      toast.error(errorMessage, toastError);
    },
  });
};

export const useCreateOrganization = (closeModal) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newOrg) => {
      let formData = new FormData();

      // append normal fields to formdata
      for (let key of Object.keys(newOrg)) {
        formData.append(key, newOrg[key]);
      }

      if (newOrg.file) {
        formData.append("logo", newOrg.file);
      }

      formData.append("id", newOrg.file);

      // create org
      const resOrg = await axiosInstance.post(`/organizations`, formData, {
        headers: {
          Authorization: authHeader(),
          "Content-Type": "multipart/form-data",
        },
      });
      return resOrg.data;
    },
    onSuccess: (data) => {
      closeModal();
      toast.success("Organizzazione aggiunta con successo!", toastSuccess);
      if (data?.warning) {
        toast.warn(data.warning, toastError);
      }
      queryClient.invalidateQueries(["organizations"]);
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error ||
        "Errore nell'aggiunta dell'organizzazione";
      toast.error(errorMessage, toastError);
    },
  });
};

export const useGetOrganizations = () => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();

  return useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      // return just the data we need if we're fetching data not as superadmin
      if (authUser()?.role !== 1) return { isSuccess: true, hasNoData: true };

      const res = await axiosInstance.get("/organizations", {
        headers: { Authorization: authHeader() },
      });

      return res.data;
    },
  });
};

export const useGetUsers = () => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await axiosInstance.get("/users", {
        headers: { Authorization: authHeader() },
      });

      return res.data;
    },
  });
};

export const useGetUserById = (user_id) => {
  const authHeader = useAuthHeader();
  const getOrganizations = useGetOrganizations();

  return useQuery({
    queryKey: ["users", user_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/users/${user_id}`, {
        headers: { Authorization: authHeader() },
      });

      let data = res.data;

      // assign organization name to display on list
      data["org_name"] =
        data.org_id === null
          ? null
          : getOrganizations.data.organizations.find(
              (el) => el.id === data.org_id,
            ).name;

      return data;
    },
    enabled: getOrganizations.isSuccess,
  });
};

export const useGetOrganizationById = (org_id) => {
  const authHeader = useAuthHeader();
  const getOrganizations = useGetOrganizations();

  return useQuery({
    queryKey: ["organizations", org_id],
    queryFn: async () => {
      if (org_id == null) {
        return null;
      }
      const res = await axiosInstance.get(`/organizations/${org_id}`, {
        headers: { Authorization: authHeader() },
      });

      return res.data;
    },
  });
};

export const useResetPassword = () => {
  const authHeader = useAuthHeader();

  return useMutation({
    mutationFn: async (value) => {
      let res;
      if (typeof value === "string") {
        res = await axiosInstance.post(
          `/users/reset_password`,
          { email: value },
          {
            headers: {
              Authorization: authHeader(),
            },
          },
        );
      } else {
        res = await axiosInstance.post(`/users/${value}/reset_password`, {
          headers: {
            Authorization: authHeader(),
          },
        });
      }
      return res;
    },
    onSuccess: () => {
      toast.success("Richiesta effettuata con successo!", toastSuccess);
    },
    onError: () => {
      toast.error("Errore nella richiesta", toastError);
    },
  });
};

export const usePutOrganization = () => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (editedOrg) => {
      let formData = new FormData();
      const org_id = editedOrg.id;

      // if just the logo
      if (editedOrg.file) {
        formData.append("logo", editedOrg.file);
      }

      // append normal fields to formdata
      for (let key of Object.keys(editedOrg)) {
        formData.append(key, editedOrg[key]);
      }

      // create org
      const resOrg = await axiosInstance.put(
        `/organizations/${org_id}`,
        formData,
        {
          headers: {
            Authorization: authHeader(),
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return resOrg.data;
    },
    onSuccess: (data) => {
      toast.success("Organizzazione modificata con successo!", toastSuccess);
      if (data?.warning) {
        toast.warn(data.warning, toastError);
      }
      queryClient.invalidateQueries(["organizations"]);
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error ||
        "Errore nella modifica dell'organizzazione";
      toast.error(errorMessage, toastError);
    },
  });
};

export const usePutUser = () => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (editedUser) => {
      const user_id = editedUser.id;
      const payload = sanitizeUserUpdatePayload(editedUser);
      const res = await axiosInstance.put(`/users/${user_id}`, payload, {
        headers: {
          Authorization: authHeader(),
        },
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Utente modificata con successo!", toastSuccess);
      queryClient.invalidateQueries(["users"]);
    },
    onError: () => {
      toast.error("Errore nella modifica dell'utente", toastError);
    },
  });
};
